import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  AddDiscountCodeStatus,
  AddDiscountCodeSucceededResult,
  AddGiftCardToCartSucceededResult,
  AddProductsToCurrentCartFailedAction,
  AddProductsToCurrentCartSucceededResult,
  AddToCurrentCartFailedAction,
  AddToCurrentCartFailedError,
  AddToCurrentCartSucceededResult,
  AsyncActionState,
  CartsState,
  ChangeCartDeliveryAddressSucceededResult,
  ChangeCartDeliveryModeSucceededResult,
  ChangeCartPaymentModeSucceededResult,
  ChangeCartSelectedStoreSucceededResult,
  ChangeCartTypeSucceededResult,
  ChangeCurrentCartBirthDateSucceededResult,
  ChangeCurrentCartContactSucceededResult,
  ChangeCurrentCartEmailSucceededResult,
  CurrentCartFailedState,
  DeleteCurrentCartEntrySucceededResult,
  DeleteCurrentCartSucceededResult,
  GiftCardCancellationStatus,
  NonAppliedDiscountCodeStatus,
  OptionalStateCarts,
  RegisterCustomerAndChangeAddressesAndSignInFailedResult,
  RemoveDiscountCodeResult,
  RemoveGiftCardFromCartSucceededResult,
  StateCart,
  StateGiftCardStatus,
  UpdateCurrentCartEntryQuantitySucceededResult
} from './CartsState';
import {
  type AddEmployeeDiscountCodeToCurrentCartsFailedPayload,
  type AddEmployeeDiscountCodeToCurrentCartsRequestedPayload,
  type AddEmployeeDiscountCodeToCurrentCartsSucceededPayload,
  addEmployeeDiscountCodeToCurrentCartsFailedReducer,
  addEmployeeDiscountCodeToCurrentCartsRequestedReducer,
  addEmployeeDiscountCodeToCurrentCartsSucceededReducer
} from './reducers/addEmployeeDiscountCodeToCurrentCarts';
import { Draft, castDraft } from 'immer';
import { HydrateAction, hydrateType } from '../HydrateAction';
import {
  type RemoveAllDiscountCodesFromCartsFailedPayload,
  type RemoveAllDiscountCodesFromCartsRequestedPayload,
  type RemoveAllDiscountCodesFromCartsSucceededPayload,
  removeAllDiscountCodesFromCartsFailedReducer,
  removeAllDiscountCodesFromCartsRequestedReducer,
  removeAllDiscountCodesFromCartsSucceededReducer
} from './reducers/removeAllDiscountCodesFromCarts';
import { ApiCartPaymentMode } from '../api/cart/changeCartPaymentModeApi';
import { ApiOrdersPlaceOrderAction } from '@spa/lib/store/api/orders/placeOrdersApi';
import { CartValidationError } from '@spa/generated/graphql/graphql';
import { CustomOrderType } from './CustomOrderType';
import { RegisterCustomerRequestedPayload } from '@spa/lib/store/auth/authSlice';
import { SliceActions } from '../SliceActions';
import { TitleCode } from '@spa/lib/converters/convertCustomerTitles';
import { UpdateUwgFlagInput } from '@spa/generated/graphql/graphql';
import { isQuantityValidationError } from '@spa/lib/cartQuantityValidation';
import removeFirstMatchingItemFromArray from '@spa/lib/store/removeFirstMatchingItemFromArray';

export const emptyPageActions: CartsState['pageActions'] = {
  addToCurrentCart: {
    pending: [],
    failed: [],
    succeeded: []
  },
  addProductsToCurrentCart: {
    pending: [],
    failed: [],
    succeeded: []
  },
  updateCurrentCartEntryQuantity: {
    pending: [],
    failed: [],
    succeeded: []
  },
  deleteCurrentCartEntry: {
    pending: [],
    failed: [],
    succeeded: []
  },
  deleteCurrentCart: {
    pending: [],
    failed: [],
    succeeded: []
  },
  addDiscountCode: {
    pending: [],
    failed: [],
    succeeded: []
  },
  addDiscountCodeToCurrentCarts: {
    pending: [],
    failed: [],
    succeeded: []
  },
  addEmployeeDiscountCodeToCurrentCarts: {
    pending: [],
    failed: [],
    succeeded: []
  },
  removeDiscountCode: {
    pending: [],
    failed: [],
    succeeded: []
  },
  removeDiscountCodeFromCarts: {
    pending: [],
    failed: [],
    succeeded: []
  },
  removeAllDiscountCodesFromCarts: {
    pending: [],
    failed: [],
    succeeded: []
  },
  addGiftCardToCurrentCart: {
    pending: [],
    failed: [],
    succeeded: []
  },
  removeGiftCardFromCurrentCart: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartEmail: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartContact: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartBirthDate: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartDeliveryAddress: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartSelectedStore: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartDeliveryMode: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartType: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartPaymentAddress: {
    pending: [],
    failed: [],
    succeeded: []
  },
  changeCurrentCartPaymentMode: {
    pending: [],
    failed: [],
    succeeded: []
  },
  placeCurrentCarts: {
    pending: [],
    failed: [],
    succeeded: []
  },
  registerCustomerAndChangeAddressesAndSignIn: {
    pending: [],
    failed: [],
    succeeded: []
  }
};

const initialStateCarts: OptionalStateCarts = {
  [CustomOrderType.COLLECT_AT_STORE]: null,
  [CustomOrderType.HOME_DELIVERY]: null
};

const initialState: CartsState = {
  carts: initialStateCarts,
  cartsLoaded: false,
  lastSyncedServerTimestamp: null,
  lastGiftCardOnlineId: null,
  giftCardRemovedBecauseCartChanged: false,
  pageActions: emptyPageActions
};

export interface AddToCurrentCartRequestedPayloadTracking {
  readonly itemListId?: string;
  readonly itemListName?: string;
}

export interface AddToCurrentCartRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly productCode: string;
  readonly quantity: number;
  readonly storeCode?: string;
  readonly tracking?: AddToCurrentCartRequestedPayloadTracking;
}

export interface AddToCurrentCartSucceededPayload {
  readonly addedQuantity: number;
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly isNewCart: boolean;
  readonly productCode: string;
  readonly requestedActionId: number;
  readonly requestedQuantity: number;
  readonly sourceCustomOrderType?: CustomOrderType;
}

export interface AddToCurrentCartFailedPayload {
  readonly requestedActionId: number;
  readonly failedReason: AddToCurrentCartFailedError;
}

export interface AddProductsToCurrentCartRequestedPayloadEntry {
  readonly code: string;
  readonly quantity: number;
  readonly tracking?: AddToCurrentCartRequestedPayloadTracking;
}
export interface AddProductsToCurrentCartSucceededPayloadEntry {
  readonly code: string;
  readonly quantity: number;
}
export interface AddProductsToCurrentCartRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly productCodesWithQuantities: Array<AddProductsToCurrentCartRequestedPayloadEntry>;
  // Fire add_to_cart for the added entries. Off for internal moves between cart types.
  readonly trackAddToCart?: boolean;
}

export interface AddProductsToCurrentCartSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly isNewCart: boolean;
  readonly requestedActionId: number;
  readonly allQuantityAdded: boolean;
  readonly productCodesWithQuantities: Array<AddProductsToCurrentCartSucceededPayloadEntry>;
  readonly sourceCustomOrderType?: CustomOrderType;
  readonly trackAddToCart?: boolean;
}

export interface AddProductsToCurrentCartFailedPayload {
  readonly requestedActionId: number;
  readonly failedReason: AddToCurrentCartFailedError;
}

export interface UpdateCurrentCartEntryQuantityRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly entryId: string;
  readonly quantity: number;
}

export interface UpdateCurrentCartEntryQuantitySucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly entryId: string;
  readonly requestedActionId: number;
  readonly requestedQuantity: number;
  readonly updatedQuantity: number;
  readonly sourceCustomOrderType?: CustomOrderType;
}

export interface UpdateCurrentCartEntryQuantityFailedPayload {
  readonly requestedActionId: number;
}

export interface DeleteCurrentCartEntryRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly entryId: string;
}

export interface DeleteCurrentCartEntrySucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly entryId: string;
  readonly requestedActionId: number;
  readonly sourceCustomOrderType?: CustomOrderType;
}

export interface DeleteCurrentCartEntryFailedPayload {
  readonly requestedActionId: number;
}

export interface DeleteCurrentCartRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
}

export interface DeleteCurrentCartSucceededPayload {
  readonly cartId: string;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface DeleteCurrentCartFailedPayload {
  readonly requestedActionId: number;
}

export interface AddDiscountCodeRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly discountCode: string;
}

export interface AddDiscountCodeSucceededPayload {
  readonly cart: StateCart | null;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
  readonly isOneTimeDiscountCode: boolean;
}

export interface AddDiscountCodeFailedPayload {
  readonly requestedActionId: number;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export interface AddDiscountCodeToCurrentCartsRequestedPayload {
  readonly requestedActionId: number;
  readonly discountCode: string;
}

export interface AddDiscountCodeToCurrentCartsSucceededPayload {
  readonly requestedActionId: number;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export interface AddDiscountCodeToCurrentCartsFailedPayload {
  readonly requestedActionId: number;
  readonly discountCode: string;
  readonly redemptionStatus: AddDiscountCodeStatus;
}

export interface RemoveDiscountCodeRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly codeId: string;
}

export interface RemoveDiscountCodeSucceededPayload {
  readonly cart: StateCart | null;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface RemoveDiscountCodeFailedPayload {
  readonly requestedActionId: number;
  readonly customOrderType: CustomOrderType;
  readonly codeId: string;
}

export interface RemoveDiscountCodeFromCartsRequestedPayload {
  readonly requestedActionId: number;
  readonly codeId: string;
}

export interface RemoveDiscountCodeFromCartsSucceededPayload {
  readonly requestedActionId: number;
  readonly codeId: string;
  readonly customOrderTypes: Array<CustomOrderType>;
}

export interface RemoveDiscountCodeFromCartsFailedPayload {
  readonly requestedActionId: number;
  readonly codeId: string;
  readonly customOrderTypes: Array<CustomOrderType>;
}

export interface AddGiftCardToCartRequestedPayload {
  readonly actionId: number;
  readonly cartId: string;
  readonly accountIdentifier: string;
  readonly pin: string;
}

export interface SetLastGiftCardOnlineIdPayload {
  readonly onlineId: string | null;
}

export interface AddGiftCardToCartSucceededPayload {
  readonly cart: StateCart | null;
  readonly requestedActionId: number;
  readonly status: StateGiftCardStatus;
  readonly onlineId: string;
}

export interface AddGiftCardToCartFailedPayload {
  readonly requestedActionId: number;
  readonly errorCode: string | null;
}

export interface RemoveGiftCardFromCartRequestedPayload {
  readonly actionId: number;
  readonly cartId: string;
  readonly isUserInitiated?: boolean;
}

export interface RemoveGiftCardFromCartSucceededPayload {
  readonly cart: StateCart | null;
  readonly requestedActionId: number;
}

export interface RemoveGiftCardFromCartFailedPayload {
  readonly requestedActionId: number;
}

export type {
  RemoveAllDiscountCodesFromCartsFailedPayload,
  RemoveAllDiscountCodesFromCartsRequestedPayload,
  RemoveAllDiscountCodesFromCartsSucceededPayload
} from './reducers/removeAllDiscountCodesFromCarts';

export interface ChangeCurrentCartEmailRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly email: string;
}

export interface ChangeCurrentCartEmailSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartEmailFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCartContactPayload {
  readonly titleCode: TitleCode;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
}

export interface ChangeCurrentCartContactRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly contact: ChangeCartContactPayload;
}

export interface ChangeCurrentCartContactSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartContactFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartBirthDateRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly birthDate: string;
}

export interface ChangeCurrentCartBirthDateSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartBirthDateFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeDeliveryAddressPayload {
  readonly titleCode: TitleCode;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly line1: string;
  readonly line2: string;
  readonly additionalAddressInfo: string | null;
  readonly postalCode: string;
  readonly town: string;
  readonly countryIsocode: string;
  readonly email: string | null;
  readonly mobile: string | null;
  readonly company: string | null;
}

export interface ChangeCurrentCartDeliveryAddressRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly address: ChangeDeliveryAddressPayload;
}

export interface ChangeCurrentCartDeliveryAddressSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartDeliveryAddressFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartSelectedStoreRequestedPayload {
  readonly actionId: number;
  readonly storeCode: string;
}

export interface ChangeCurrentCartSelectedStoreSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType.COLLECT_AT_STORE;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartSelectedStoreFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartPaymentModeRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly paymentMode: ApiCartPaymentMode;
}

export interface ChangeCurrentCartPaymentModeSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartPaymentModeFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartDeliveryModeRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
}

export interface ChangeCurrentCartDeliveryModeSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartDeliveryModeFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartTypeRequestedPayload {
  readonly actionId: number;
  readonly targetCustomOrderType: CustomOrderType;
}

export interface ChangeCurrentCartTypeSucceededPayload {
  readonly cart: StateCart;
  readonly sourceCustomOrderType: CustomOrderType;
  readonly targetCustomOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartTypeFailedPayload {
  readonly requestedActionId: number;
}

export interface ChangePaymentAddressPayload {
  readonly titleCode: TitleCode;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly line1: string;
  readonly line2: string;
  readonly additionalAddressInfo: string | null;
  readonly town: string;
  readonly postalCode: string;
  readonly countryIsocode: string;
  readonly email: string;
  readonly company: string | null;
}

export interface ChangeCurrentCartPaymentAddressRequestedPayload {
  readonly actionId: number;
  readonly customOrderType: CustomOrderType;
  readonly address: ChangePaymentAddressPayload;
}

export interface ChangeCurrentCartPaymentAddressSucceededPayload {
  readonly cart: StateCart;
  readonly customOrderType: CustomOrderType;
  readonly requestedActionId: number;
}

export interface ChangeCurrentCartPaymentAddressFailedPayload {
  readonly requestedActionId: number;
}

export interface PlaceCurrentCartsRequestedPayload {
  readonly actionId: number;
  readonly additionalData: { [key: string]: string | undefined };
  readonly emarsysParams: UpdateUwgFlagInput;
}

export interface PlaceOrderSucceededResult {
  readonly cartId: string;
  readonly succeeded: true;
  readonly orderId: string;
}

export interface PlaceOrderFailedResult {
  readonly succeeded: false;
  readonly action: ApiOrdersPlaceOrderAction | null;
  readonly error: string;
  readonly giftCardCancellation: GiftCardCancellationStatus | null;
}

export type PlaceOrderResult = PlaceOrderSucceededResult | PlaceOrderFailedResult;

export interface PlaceCurrentCartsSucceededPayload {
  readonly requestedActionId: number;
  readonly homeDelivery: PlaceOrderResult | null;
  readonly collectAtStore: PlaceOrderResult | null;
}

export interface PlaceOrderRedirectAction {
  readonly type: 'redirect';
  readonly url: string;
}

export interface PlaceOrderDisplayKlarnaWidgetAction {
  readonly type: 'display-klarna-widget';
  readonly clientToken: string;
}

export type PlaceOrderAction = PlaceOrderRedirectAction | PlaceOrderDisplayKlarnaWidgetAction;

export interface PlaceCurrentCartsFailedPayload {
  readonly requestedActionId: number;
  readonly homeDelivery: PlaceOrderResult | null;
  readonly collectAtStore: PlaceOrderResult | null;
}

export interface RegisterCustomerAndChangeAddressesAndSignInRequestedPayload {
  readonly actionId: number;
  readonly register: Omit<RegisterCustomerRequestedPayload, 'actionId'>;
  readonly contacts: ReadonlyArray<Omit<ChangeCurrentCartContactRequestedPayload, 'actionId'>>;
  readonly emails: ReadonlyArray<Omit<ChangeCurrentCartEmailRequestedPayload, 'actionId'>>;
}

export interface RegisterCustomerAndChangeAddressesAndSignInSucceededPayload {
  readonly requestedActionId: number;
}

export type RegisterCustomerAndChangeAddressesAndSignInFailedPayload =
  RegisterCustomerAndChangeAddressesAndSignInFailedResult;

export interface CurrentCartsChangedOutsideOfAppPayload {
  readonly carts: OptionalStateCarts;
}

export interface SetCartPayload {
  readonly localChange: boolean;
  readonly cart: StateCart | null;
  readonly customOrderType: CustomOrderType;
}

const updateFailedActionState = <R, F extends CurrentCartFailedState = CurrentCartFailedState>(
  { failed, pending }: Draft<AsyncActionState<R, F>>,
  failedReason: F
): void => {
  removeFirstMatchingItemFromArray(pending, failedReason.requestedActionId);
  (failed as Draft<F>[]).push(castDraft(failedReason));
};

export const cartsSlice = createSlice({
  name: 'carts',
  initialState,
  reducers: {
    cartQuantityValidationFailed: (
      state: Draft<CartsState>,
      {
        payload: { cartId, errors }
      }: PayloadAction<{ cartId: string; errors: ReadonlyArray<CartValidationError> }>
    ): void => {
      const cart = Object.values(state.carts).find((candidate) => candidate?.code === cartId);
      if (cart) {
        for (const error of errors) {
          if (isQuantityValidationError(error.message) && error.values?.quantity != null) {
            for (const entry of cart.entries) {
              if (error.entryIds.includes(entry.id)) {
                entry.product.saleMinimumQuantity =
                  error.values.minimumQuantity ?? entry.product.saleMinimumQuantity;
                entry.product.saleQuantityIncrements =
                  error.values.quantityIncrement ?? entry.product.saleQuantityIncrements;
                entry.quantityValidationError = {
                  message: error.message,
                  values: {
                    quantity: error.values.quantity,
                    minimumQuantity: entry.product.saleMinimumQuantity,
                    quantityIncrement: entry.product.saleQuantityIncrements
                  }
                };
              }
            }
          }
        }
      }
    },
    setCart: (
      state: Draft<CartsState>,
      { payload: { cart, customOrderType } }: PayloadAction<SetCartPayload>
    ): void => {
      state.cartsLoaded = true;
      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartEmailRequested: (
      {
        pageActions: {
          changeCurrentCartEmail: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartEmailRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartEmailSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartEmailSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<ChangeCurrentCartEmailSucceededResult>> =
        state.pageActions.changeCurrentCartEmail;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartEmailFailed: (
      { pageActions: { changeCurrentCartEmail } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartEmailFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartEmail, {
        requestedActionId
      }),
    changeCurrentCartContactRequested: (
      {
        pageActions: {
          changeCurrentCartContact: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartContactRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartContactSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartContactSucceededPayload>
    ): void => {
      const {
        pending,
        succeeded
      }: Draft<AsyncActionState<ChangeCurrentCartContactSucceededResult>> =
        state.pageActions.changeCurrentCartContact;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartContactFailed: (
      { pageActions: { changeCurrentCartContact } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartContactFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartContact, {
        requestedActionId
      }),
    changeCurrentCartBirthDateRequested: (
      {
        pageActions: {
          changeCurrentCartBirthDate: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartBirthDateRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartBirthDateSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartBirthDateSucceededPayload>
    ): void => {
      const {
        pending,
        succeeded
      }: Draft<AsyncActionState<ChangeCurrentCartBirthDateSucceededResult>> =
        state.pageActions.changeCurrentCartBirthDate;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartBirthDateFailed: (
      { pageActions: { changeCurrentCartBirthDate } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartBirthDateFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartBirthDate, {
        requestedActionId
      }),
    addToCurrentCartRequested: (
      {
        pageActions: {
          addToCurrentCart: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<AddToCurrentCartRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    addToCurrentCartSucceeded: (
      state,
      {
        payload: {
          addedQuantity,
          cart,
          customOrderType,
          isNewCart,
          productCode,
          requestedActionId,
          requestedQuantity,
          sourceCustomOrderType
        }
      }: PayloadAction<AddToCurrentCartSucceededPayload>
    ): void => {
      const {
        succeeded,
        pending
      }: Draft<AsyncActionState<AddToCurrentCartSucceededResult, AddToCurrentCartFailedAction>> =
        state.pageActions.addToCurrentCart;
      succeeded.push({
        addedQuantity,
        cartId: cart.code,
        isNewCart,
        productCode,
        requestedActionId,
        requestedQuantity
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
      if (sourceCustomOrderType !== undefined && sourceCustomOrderType !== customOrderType) {
        state.carts[sourceCustomOrderType] = null;
      }
    },
    addToCurrentCartFailed: (
      { pageActions: { addToCurrentCart } }: Draft<CartsState>,
      { payload: { requestedActionId, failedReason } }: PayloadAction<AddToCurrentCartFailedPayload>
    ): void => {
      updateFailedActionState(addToCurrentCart, {
        requestedActionId,
        failedReason
      });
    },
    addProductsToCurrentCartRequested: (
      {
        pageActions: {
          addProductsToCurrentCart: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<AddProductsToCurrentCartRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    addProductsToCurrentCartSucceeded: (
      state,
      {
        payload: {
          cart,
          customOrderType,
          isNewCart,
          requestedActionId,
          productCodesWithQuantities,
          allQuantityAdded,
          sourceCustomOrderType
        }
      }: PayloadAction<AddProductsToCurrentCartSucceededPayload>
    ): void => {
      const {
        succeeded,
        pending
      }: Draft<
        AsyncActionState<
          AddProductsToCurrentCartSucceededResult,
          AddProductsToCurrentCartFailedAction
        >
      > = state.pageActions.addProductsToCurrentCart;
      succeeded.push({
        cartId: cart.code,
        isNewCart,
        requestedActionId,
        productCodesWithQuantities,
        allQuantityAdded
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
      if (sourceCustomOrderType !== undefined && sourceCustomOrderType !== customOrderType) {
        state.carts[sourceCustomOrderType] = null;
      }
    },
    addProductsToCurrentCartFailed: (
      { pageActions: { addProductsToCurrentCart } }: Draft<CartsState>,
      {
        payload: { requestedActionId, failedReason }
      }: PayloadAction<AddProductsToCurrentCartFailedPayload>
    ): void => {
      updateFailedActionState(addProductsToCurrentCart, {
        requestedActionId,
        failedReason
      });
    },
    updateCurrentCartEntryQuantityRequested: (
      {
        pageActions: {
          updateCurrentCartEntryQuantity: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<UpdateCurrentCartEntryQuantityRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    updateCurrentCartEntryQuantitySucceeded: (
      state: Draft<CartsState>,
      {
        payload: {
          cart,
          customOrderType,
          entryId,
          requestedActionId,
          requestedQuantity,
          updatedQuantity,
          sourceCustomOrderType
        }
      }: PayloadAction<UpdateCurrentCartEntryQuantitySucceededPayload>
    ): void => {
      const {
        pending,
        succeeded
      }: Draft<AsyncActionState<UpdateCurrentCartEntryQuantitySucceededResult>> =
        state.pageActions.updateCurrentCartEntryQuantity;
      succeeded.push({
        cartId: cart.code,
        entryId,
        requestedActionId,
        requestedQuantity,
        updatedQuantity
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
      if (sourceCustomOrderType !== undefined && sourceCustomOrderType !== customOrderType) {
        state.carts[sourceCustomOrderType] = null;
      }
    },
    updateCurrentCartEntryQuantityFailed: (
      { pageActions: { updateCurrentCartEntryQuantity } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<UpdateCurrentCartEntryQuantityFailedPayload>
    ): void =>
      updateFailedActionState(updateCurrentCartEntryQuantity, {
        requestedActionId
      }),
    deleteCurrentCartEntryRequested: (
      {
        pageActions: {
          deleteCurrentCartEntry: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<DeleteCurrentCartEntryRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    deleteCurrentCartEntrySucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, entryId, requestedActionId, sourceCustomOrderType }
      }: PayloadAction<DeleteCurrentCartEntrySucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<DeleteCurrentCartEntrySucceededResult>> =
        state.pageActions.deleteCurrentCartEntry;
      succeeded.push({ cartId: cart.code, entryId, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
      if (sourceCustomOrderType !== undefined && sourceCustomOrderType !== customOrderType) {
        state.carts[sourceCustomOrderType] = null;
      }
    },
    deleteCurrentCartEntryFailed: (
      { pageActions: { deleteCurrentCartEntry } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<DeleteCurrentCartEntryFailedPayload>
    ): void =>
      updateFailedActionState(deleteCurrentCartEntry, {
        requestedActionId
      }),

    deleteCurrentCartRequested: (
      {
        pageActions: {
          deleteCurrentCart: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<DeleteCurrentCartRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    deleteCurrentCartSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cartId, customOrderType, requestedActionId }
      }: PayloadAction<DeleteCurrentCartSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<DeleteCurrentCartSucceededResult>> =
        state.pageActions.deleteCurrentCart;
      succeeded.push({ cartId, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = null;
    },
    deleteCurrentCartFailed: (
      { pageActions: { deleteCurrentCart } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<DeleteCurrentCartFailedPayload>
    ): void =>
      updateFailedActionState(deleteCurrentCart, {
        requestedActionId
      }),
    changeCurrentCartDeliveryAddressRequested: (
      {
        pageActions: {
          changeCurrentCartDeliveryAddress: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartDeliveryAddressRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    addDiscountCodeRequested: (
      {
        pageActions: {
          addDiscountCode: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<AddDiscountCodeRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    addDiscountCodeSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId, discountCode, redemptionStatus }
      }: PayloadAction<AddDiscountCodeSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<AddDiscountCodeSucceededResult>> =
        state.pageActions.addDiscountCode;
      succeeded.push({
        cartId: cart?.code ?? null,
        discountCode,
        requestedActionId,
        redemptionStatus
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      state.carts[customOrderType] = castDraft(cart);
    },
    addDiscountCodeFailed: (
      { pageActions: { addDiscountCode } }: Draft<CartsState>,
      {
        payload: { requestedActionId, redemptionStatus }
      }: PayloadAction<AddDiscountCodeFailedPayload>
    ): void =>
      updateFailedActionState(addDiscountCode, {
        requestedActionId,
        failedReason: redemptionStatus as NonAppliedDiscountCodeStatus
      }),
    addDiscountCodeToCurrentCartsRequested: (
      {
        pageActions: {
          addDiscountCodeToCurrentCarts: { pending }
        }
      }: Draft<CartsState>,
      {
        payload: { requestedActionId }
      }: PayloadAction<AddDiscountCodeToCurrentCartsRequestedPayload>
    ): void => {
      pending.push(requestedActionId);
    },
    addDiscountCodeToCurrentCartsSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { requestedActionId, redemptionStatus, discountCode }
      }: PayloadAction<AddDiscountCodeToCurrentCartsSucceededPayload>
    ): void => {
      const { pending, succeeded } = state.pageActions.addDiscountCodeToCurrentCarts;
      succeeded.push({ requestedActionId, discountCode, redemptionStatus });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    addDiscountCodeToCurrentCartsFailed: (
      { pageActions: { addDiscountCodeToCurrentCarts } }: Draft<CartsState>,
      {
        payload: { requestedActionId, redemptionStatus, discountCode }
      }: PayloadAction<AddDiscountCodeToCurrentCartsFailedPayload>
    ): void => {
      const { pending, failed } = addDiscountCodeToCurrentCarts;
      failed.push({
        requestedActionId,
        redemptionStatus,
        discountCode
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    addEmployeeDiscountCodeToCurrentCartsRequested:
      addEmployeeDiscountCodeToCurrentCartsRequestedReducer,
    addEmployeeDiscountCodeToCurrentCartsSucceeded:
      addEmployeeDiscountCodeToCurrentCartsSucceededReducer,
    addEmployeeDiscountCodeToCurrentCartsFailed: addEmployeeDiscountCodeToCurrentCartsFailedReducer,
    removeDiscountCodeRequested: (
      {
        pageActions: {
          removeDiscountCode: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<RemoveDiscountCodeRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    removeDiscountCodeSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<RemoveDiscountCodeSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<RemoveDiscountCodeResult>> =
        state.pageActions.removeDiscountCode;
      succeeded.push({
        cartId: cart?.code ?? null,
        requestedActionId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      state.carts[customOrderType] = castDraft(cart);
    },
    removeDiscountCodeFailed: (
      { pageActions: { removeDiscountCode } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<RemoveDiscountCodeFailedPayload>
    ): void =>
      updateFailedActionState(removeDiscountCode, {
        requestedActionId
      }),
    removeDiscountCodeFromCartsRequested: (
      {
        pageActions: {
          removeDiscountCodeFromCarts: { pending }
        }
      }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<RemoveDiscountCodeFromCartsRequestedPayload>
    ): void => {
      pending.push(requestedActionId);
    },
    removeDiscountCodeFromCartsSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { requestedActionId, codeId, customOrderTypes }
      }: PayloadAction<RemoveDiscountCodeFromCartsSucceededPayload>
    ): void => {
      const { pending, succeeded } = state.pageActions.removeDiscountCodeFromCarts;
      succeeded.push({
        errorsOn: [],
        removedFrom: customOrderTypes,
        requestedActionId,
        codeId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    removeDiscountCodeFromCartsFailed: (
      { pageActions: { removeDiscountCodeFromCarts } }: Draft<CartsState>,
      {
        payload: { requestedActionId, codeId, customOrderTypes }
      }: PayloadAction<RemoveDiscountCodeFromCartsFailedPayload>
    ): void => {
      const { pending, failed } = removeDiscountCodeFromCarts;
      failed.push({
        errorsOn: customOrderTypes,
        removedFrom: [],
        requestedActionId,
        codeId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    removeAllDiscountCodesFromCartsRequested: removeAllDiscountCodesFromCartsRequestedReducer,
    removeAllDiscountCodesFromCartsSucceeded: removeAllDiscountCodesFromCartsSucceededReducer,
    removeAllDiscountCodesFromCartsFailed: removeAllDiscountCodesFromCartsFailedReducer,
    setLastGiftCardOnlineId: (
      state: Draft<CartsState>,
      { payload: { onlineId } }: PayloadAction<SetLastGiftCardOnlineIdPayload>
    ): void => {
      state.lastGiftCardOnlineId = onlineId;
    },
    addGiftCardToCartRequested: (
      state: Draft<CartsState>,
      { payload: { actionId, accountIdentifier } }: PayloadAction<AddGiftCardToCartRequestedPayload>
    ): void => {
      state.pageActions.addGiftCardToCurrentCart.pending.push(actionId);
      state.lastGiftCardOnlineId = accountIdentifier;
    },
    addGiftCardToCartSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, requestedActionId, status, onlineId }
      }: PayloadAction<AddGiftCardToCartSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<AddGiftCardToCartSucceededResult>> =
        state.pageActions.addGiftCardToCurrentCart;
      succeeded.push({
        cartId: cart?.code ?? null,
        requestedActionId,
        status: castDraft(status),
        onlineId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      state.giftCardRemovedBecauseCartChanged = false;
      if (cart) {
        const customOrderType = (Object.keys(state.carts) as CustomOrderType[]).find(
          (type) => state.carts[type]?.code === cart.code
        );
        if (customOrderType !== undefined) {
          state.carts[customOrderType] = castDraft(cart);
        }
      }
    },
    addGiftCardToCartFailed: (
      { pageActions: { addGiftCardToCurrentCart } }: Draft<CartsState>,
      { payload: { requestedActionId, errorCode } }: PayloadAction<AddGiftCardToCartFailedPayload>
    ): void =>
      updateFailedActionState(addGiftCardToCurrentCart, {
        requestedActionId,
        errorCode
      }),
    removeGiftCardFromCartRequested: (
      state: Draft<CartsState>,
      {
        payload: { actionId, cartId, isUserInitiated }
      }: PayloadAction<RemoveGiftCardFromCartRequestedPayload>
    ): void => {
      state.pageActions.removeGiftCardFromCurrentCart.pending.push(actionId);
      if (isUserInitiated) {
        state.lastGiftCardOnlineId = null;
      } else {
        const cart: Draft<StateCart> | null | undefined = Object.values(state.carts).find(
          (candidate): boolean => candidate?.code === cartId
        );
        const appliedOnlineId: string | null =
          cart?.payments?.find(({ giftCard }): boolean => Boolean(giftCard?.onlineId))?.giftCard
            ?.onlineId ?? null;
        if (appliedOnlineId !== null) {
          state.lastGiftCardOnlineId = appliedOnlineId;
        }
      }
    },
    removeGiftCardFromCartSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, requestedActionId }
      }: PayloadAction<RemoveGiftCardFromCartSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<RemoveGiftCardFromCartSucceededResult>> =
        state.pageActions.removeGiftCardFromCurrentCart;
      succeeded.push({
        cartId: cart?.code ?? null,
        requestedActionId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
      if (cart) {
        const customOrderType = (Object.keys(state.carts) as CustomOrderType[]).find(
          (type) => state.carts[type]?.code === cart.code
        );
        if (customOrderType !== undefined) {
          state.carts[customOrderType] = castDraft(cart);
        }
      }
    },
    removeGiftCardFromCartFailed: (
      { pageActions: { removeGiftCardFromCurrentCart } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<RemoveGiftCardFromCartFailedPayload>
    ): void =>
      updateFailedActionState(removeGiftCardFromCurrentCart, {
        requestedActionId
      }),
    changeCurrentCartDeliveryAddressSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartDeliveryAddressSucceededPayload>
    ): void => {
      const {
        pending,
        succeeded
      }: Draft<AsyncActionState<ChangeCartDeliveryAddressSucceededResult>> =
        state.pageActions.changeCurrentCartDeliveryAddress;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartDeliveryAddressFailed: (
      { pageActions: { changeCurrentCartDeliveryAddress } }: Draft<CartsState>,
      {
        payload: { requestedActionId }
      }: PayloadAction<ChangeCurrentCartDeliveryAddressFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartDeliveryAddress, {
        requestedActionId
      }),
    changeCurrentCartSelectedStoreRequested: (
      {
        pageActions: {
          changeCurrentCartSelectedStore: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartSelectedStoreRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartSelectedStoreSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartSelectedStoreSucceededPayload>
    ): void => {
      const {
        pending,
        succeeded
      }: Draft<AsyncActionState<ChangeCartSelectedStoreSucceededResult>> =
        state.pageActions.changeCurrentCartSelectedStore;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartSelectedStoreFailed: (
      { pageActions: { changeCurrentCartSelectedStore } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartSelectedStoreFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartSelectedStore, {
        requestedActionId
      }),
    changeCurrentCartPaymentModeRequested: (
      {
        pageActions: {
          changeCurrentCartPaymentMode: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartPaymentModeRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartPaymentModeSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartPaymentModeSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<ChangeCartPaymentModeSucceededResult>> =
        state.pageActions.changeCurrentCartPaymentMode;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartPaymentModeFailed: (
      { pageActions: { changeCurrentCartPaymentMode } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartPaymentModeFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartPaymentMode, {
        requestedActionId
      }),
    changeCurrentCartDeliveryModeRequested: (
      {
        pageActions: {
          changeCurrentCartDeliveryMode: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartDeliveryModeRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartDeliveryModeSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartDeliveryModeSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<ChangeCartDeliveryModeSucceededResult>> =
        state.pageActions.changeCurrentCartDeliveryMode;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartDeliveryModeFailed: (
      { pageActions: { changeCurrentCartDeliveryMode } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartDeliveryModeFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartDeliveryMode, {
        requestedActionId
      }),
    changeCurrentCartTypeRequested: (
      {
        pageActions: {
          changeCurrentCartType: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartTypeRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartTypeSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, sourceCustomOrderType, targetCustomOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartTypeSucceededPayload>
    ): void => {
      const { pending, succeeded }: Draft<AsyncActionState<ChangeCartTypeSucceededResult>> =
        state.pageActions.changeCurrentCartType;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[targetCustomOrderType] = castDraft(cart);
      if (sourceCustomOrderType !== targetCustomOrderType) {
        state.carts[sourceCustomOrderType] = null;
      }
    },
    changeCurrentCartTypeFailed: (
      { pageActions: { changeCurrentCartType } }: Draft<CartsState>,
      { payload: { requestedActionId } }: PayloadAction<ChangeCurrentCartTypeFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartType, {
        requestedActionId
      }),
    changeCurrentCartPaymentAddressRequested: (
      {
        pageActions: {
          changeCurrentCartPaymentAddress: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<ChangeCurrentCartPaymentAddressRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    changeCurrentCartPaymentAddressSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { cart, customOrderType, requestedActionId }
      }: PayloadAction<ChangeCurrentCartPaymentAddressSucceededPayload>
    ): void => {
      const { pending, succeeded } = state.pageActions.changeCurrentCartPaymentAddress;
      succeeded.push({ cartId: cart.code, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      state.carts[customOrderType] = castDraft(cart);
    },
    changeCurrentCartPaymentAddressFailed: (
      { pageActions: { changeCurrentCartPaymentAddress } }: Draft<CartsState>,
      {
        payload: { requestedActionId }
      }: PayloadAction<ChangeCurrentCartPaymentAddressFailedPayload>
    ): void =>
      updateFailedActionState(changeCurrentCartPaymentAddress, {
        requestedActionId
      }),
    placeCurrentCartsRequested: (
      {
        pageActions: {
          placeCurrentCarts: { pending }
        }
      }: Draft<CartsState>,
      { payload: { actionId } }: PayloadAction<PlaceCurrentCartsRequestedPayload>
    ): void => {
      pending.push(actionId);
    },
    placeCurrentCartsSucceeded: (
      state: Draft<CartsState>,
      {
        payload: { collectAtStore, homeDelivery, requestedActionId }
      }: PayloadAction<PlaceCurrentCartsSucceededPayload>
    ): void => {
      const { pending, succeeded } = state.pageActions.placeCurrentCarts;
      succeeded.push({ collectAtStore, homeDelivery, requestedActionId });
      removeFirstMatchingItemFromArray(pending, requestedActionId);

      if (homeDelivery !== null) {
        state.carts[CustomOrderType.HOME_DELIVERY] = null;
      }
      if (collectAtStore !== null) {
        state.carts[CustomOrderType.COLLECT_AT_STORE] = null;
      }
      state.lastGiftCardOnlineId = null;
    },
    placeCurrentCartsFailed: (
      { carts, pageActions: { placeCurrentCarts } }: Draft<CartsState>,
      {
        payload: { requestedActionId, homeDelivery, collectAtStore }
      }: PayloadAction<PlaceCurrentCartsFailedPayload>
    ): void => {
      const { pending, failed } = placeCurrentCarts;
      failed.push({
        requestedActionId,
        homeDelivery,
        collectAtStore
      });
      if (homeDelivery?.succeeded === true) {
        carts[CustomOrderType.HOME_DELIVERY] = null;
      }
      if (collectAtStore?.succeeded === true) {
        carts[CustomOrderType.COLLECT_AT_STORE] = null;
      }

      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    registerCustomerAndChangeAddressesAndSignInRequested: (
      {
        pageActions: {
          registerCustomerAndChangeAddressesAndSignIn: { pending }
        }
      }: Draft<CartsState>,
      {
        payload: { actionId }
      }: PayloadAction<RegisterCustomerAndChangeAddressesAndSignInRequestedPayload>
    ) => {
      pending.push(actionId);
    },
    registerCustomerAndChangeAddressesAndSignInSucceeded: (
      { pageActions: { registerCustomerAndChangeAddressesAndSignIn } }: Draft<CartsState>,
      {
        payload: { requestedActionId }
      }: PayloadAction<RegisterCustomerAndChangeAddressesAndSignInSucceededPayload>
    ) => {
      const { pending, succeeded } = registerCustomerAndChangeAddressesAndSignIn;
      succeeded.push({
        requestedActionId
      });
      removeFirstMatchingItemFromArray(pending, requestedActionId);
    },
    registerCustomerAndChangeAddressesAndSignInFailed: (
      { pageActions: { registerCustomerAndChangeAddressesAndSignIn } }: Draft<CartsState>,
      { payload }: PayloadAction<RegisterCustomerAndChangeAddressesAndSignInFailedPayload>
    ) => {
      const { pending, failed } = registerCustomerAndChangeAddressesAndSignIn;
      failed.push(payload);
      removeFirstMatchingItemFromArray(pending, payload.requestedActionId);
    },
    currentCartsChangedOutsideOfApp: (
      state: Draft<CartsState>,
      { payload }: PayloadAction<CurrentCartsChangedOutsideOfAppPayload>
    ): void => {
      state.cartsLoaded = true;
      state.carts = castDraft(payload.carts);
    },
    giftCardRemovedBecauseCartChanged: (state: Draft<CartsState>): void => {
      state.giftCardRemovedBecauseCartChanged = true;
    },
    giftCardRemovedBecauseCartChangeAcknowledged: (state: Draft<CartsState>): void => {
      state.giftCardRemovedBecauseCartChanged = false;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<CartsState>): void => {
    builder
      .addCase(hydrateType, (state: Draft<CartsState>, { payload }: HydrateAction): void => {
        if (!payload.carts || !payload.router) {
          return;
        }
        const { carts } = payload.carts;
        const { lastPageLoadServerTimeStampSecs } = payload.router;
        const shouldSyncServerCarts =
          lastPageLoadServerTimeStampSecs === null ||
          state.lastSyncedServerTimestamp === null ||
          lastPageLoadServerTimeStampSecs > state.lastSyncedServerTimestamp;

        if (shouldSyncServerCarts) {
          const incomingCarts = { ...carts };
          for (const cartType of Object.values(CustomOrderType)) {
            const previousCart = state.carts[cartType];
            const incomingCart = incomingCarts[cartType];
            if (incomingCart && previousCart?.code === incomingCart.code) {
              incomingCarts[cartType] = {
                ...incomingCart,
                entries: incomingCart.entries.map((entry) => {
                  const previousEntry = previousCart.entries.find(({ id }) => id === entry.id);
                  const error = previousEntry?.quantityValidationError;
                  const hasIncomingQuantityRules =
                    entry.product.saleMinimumQuantity !== null ||
                    entry.product.saleQuantityIncrements !== null;
                  return error &&
                    error.values.quantity === entry.quantity &&
                    !hasIncomingQuantityRules
                    ? {
                        ...entry,
                        quantityValidationError: error,
                        product: {
                          ...entry.product,
                          saleMinimumQuantity:
                            error.values.minimumQuantity ?? entry.product.saleMinimumQuantity,
                          saleQuantityIncrements:
                            error.values.quantityIncrement ?? entry.product.saleQuantityIncrements
                        }
                      }
                    : entry;
                })
              };
            }
          }
          state.carts = castDraft(incomingCarts);
          state.lastSyncedServerTimestamp = lastPageLoadServerTimeStampSecs;
        }
        state.pageActions = castDraft(emptyPageActions);
      })
      .addDefaultCase((): void => {});
  }
});

export const {
  cartQuantityValidationFailed,
  addToCurrentCartFailed,
  addToCurrentCartRequested,
  addToCurrentCartSucceeded,
  addProductsToCurrentCartFailed,
  addProductsToCurrentCartRequested,
  addProductsToCurrentCartSucceeded,
  updateCurrentCartEntryQuantityFailed,
  updateCurrentCartEntryQuantityRequested,
  updateCurrentCartEntryQuantitySucceeded,
  deleteCurrentCartEntryFailed,
  deleteCurrentCartEntryRequested,
  deleteCurrentCartEntrySucceeded,
  deleteCurrentCartFailed,
  deleteCurrentCartRequested,
  deleteCurrentCartSucceeded,
  addDiscountCodeRequested,
  addDiscountCodeSucceeded,
  addDiscountCodeFailed,
  addDiscountCodeToCurrentCartsRequested,
  addDiscountCodeToCurrentCartsSucceeded,
  addDiscountCodeToCurrentCartsFailed,
  changeCurrentCartBirthDateRequested,
  changeCurrentCartBirthDateSucceeded,
  changeCurrentCartBirthDateFailed,
  addEmployeeDiscountCodeToCurrentCartsRequested,
  addEmployeeDiscountCodeToCurrentCartsSucceeded,
  addEmployeeDiscountCodeToCurrentCartsFailed,
  changeCurrentCartEmailRequested,
  changeCurrentCartEmailSucceeded,
  changeCurrentCartEmailFailed,
  changeCurrentCartContactRequested,
  changeCurrentCartContactSucceeded,
  changeCurrentCartContactFailed,
  changeCurrentCartPaymentModeRequested,
  changeCurrentCartPaymentModeSucceeded,
  changeCurrentCartPaymentModeFailed,
  changeCurrentCartDeliveryAddressRequested,
  changeCurrentCartDeliveryAddressSucceeded,
  changeCurrentCartDeliveryAddressFailed,
  changeCurrentCartSelectedStoreRequested,
  changeCurrentCartSelectedStoreSucceeded,
  changeCurrentCartSelectedStoreFailed,
  changeCurrentCartDeliveryModeRequested,
  changeCurrentCartDeliveryModeSucceeded,
  changeCurrentCartDeliveryModeFailed,
  changeCurrentCartTypeRequested,
  changeCurrentCartTypeSucceeded,
  changeCurrentCartTypeFailed,
  changeCurrentCartPaymentAddressRequested,
  changeCurrentCartPaymentAddressSucceeded,
  changeCurrentCartPaymentAddressFailed,
  placeCurrentCartsRequested,
  placeCurrentCartsSucceeded,
  placeCurrentCartsFailed,
  registerCustomerAndChangeAddressesAndSignInFailed,
  registerCustomerAndChangeAddressesAndSignInRequested,
  registerCustomerAndChangeAddressesAndSignInSucceeded,
  removeDiscountCodeRequested,
  removeDiscountCodeSucceeded,
  removeDiscountCodeFailed,
  removeDiscountCodeFromCartsRequested,
  removeDiscountCodeFromCartsSucceeded,
  removeDiscountCodeFromCartsFailed,
  removeAllDiscountCodesFromCartsRequested,
  removeAllDiscountCodesFromCartsSucceeded,
  removeAllDiscountCodesFromCartsFailed,
  setLastGiftCardOnlineId,
  addGiftCardToCartRequested,
  addGiftCardToCartSucceeded,
  addGiftCardToCartFailed,
  removeGiftCardFromCartRequested,
  removeGiftCardFromCartSucceeded,
  removeGiftCardFromCartFailed,
  currentCartsChangedOutsideOfApp,
  giftCardRemovedBecauseCartChanged,
  giftCardRemovedBecauseCartChangeAcknowledged,
  setCart
} = cartsSlice.actions;

export default cartsSlice.reducer;

export type ActionTypes = SliceActions<typeof cartsSlice.actions>;

export const setCartType = 'carts/setCart';
export type SetCartAction = Extract<ActionTypes, PayloadAction<SetCartPayload, typeof setCartType>>;

export const currentCartsChangedOutsideOfAppType = 'carts/currentCartsChangedOutsideOfApp';
export type CurrentCartsChangedOutsideOfAppAction = Extract<
  ActionTypes,
  PayloadAction<CurrentCartsChangedOutsideOfAppPayload, typeof currentCartsChangedOutsideOfAppType>
>;

export const giftCardRemovedBecauseCartChangedType = 'carts/giftCardRemovedBecauseCartChanged';
export type GiftCardRemovedBecauseCartChangedAction = Extract<
  ActionTypes,
  PayloadAction<void, typeof giftCardRemovedBecauseCartChangedType>
>;

export const giftCardRemovedBecauseCartChangeAcknowledgedType =
  'carts/giftCardRemovedBecauseCartChangeAcknowledged';
export type GiftCardRemovedBecauseCartChangeAcknowledgedAction = Extract<
  ActionTypes,
  PayloadAction<void, typeof giftCardRemovedBecauseCartChangeAcknowledgedType>
>;

export const addToCurrentCartRequestedType = 'carts/addToCurrentCartRequested';
export type AddToCurrentCartRequestedAction = Extract<
  ActionTypes,
  PayloadAction<AddToCurrentCartRequestedPayload, typeof addToCurrentCartRequestedType>
>;

export const addToCurrentCartSucceededType = 'carts/addToCurrentCartSucceeded';
export type AddToCurrentCartSucceededAction = Extract<
  ActionTypes,
  PayloadAction<AddToCurrentCartSucceededPayload, typeof addToCurrentCartSucceededType>
>;

export const addProductsToCurrentCartRequestedType = 'carts/addProductsToCurrentCartRequested';
export type AddProductsToCurrentCartRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    AddProductsToCurrentCartRequestedPayload,
    typeof addProductsToCurrentCartRequestedType
  >
>;

export const addProductsToCurrentCartSucceededType = 'carts/addProductsToCurrentCartSucceeded';
export type AddProductsToCurrentCartSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    AddProductsToCurrentCartSucceededPayload,
    typeof addProductsToCurrentCartSucceededType
  >
>;

export const updateCurrentCartEntryQuantityRequestedType =
  'carts/updateCurrentCartEntryQuantityRequested';
export type UpdateCurrentCartEntryQuantityRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    UpdateCurrentCartEntryQuantityRequestedPayload,
    typeof updateCurrentCartEntryQuantityRequestedType
  >
>;

export const updateCurrentCartEntryQuantitySucceededType =
  'carts/updateCurrentCartEntryQuantitySucceeded';
export type UpdateCurrentCartEntryQuantitySucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    UpdateCurrentCartEntryQuantitySucceededPayload,
    typeof updateCurrentCartEntryQuantitySucceededType
  >
>;

export const deleteCurrentCartEntryRequestedType = 'carts/deleteCurrentCartEntryRequested';
export type DeleteCurrentCartEntryRequestedAction = Extract<
  ActionTypes,
  PayloadAction<DeleteCurrentCartEntryRequestedPayload, typeof deleteCurrentCartEntryRequestedType>
>;

export const deleteCurrentCartEntrySucceededType = 'carts/deleteCurrentCartEntrySucceeded';
export type DeleteCurrentCartEntrySucceededAction = Extract<
  ActionTypes,
  PayloadAction<DeleteCurrentCartEntrySucceededPayload, typeof deleteCurrentCartEntrySucceededType>
>;

export const deleteCurrentCartRequestedType = 'carts/deleteCurrentCartRequested';
export type DeleteCurrentCartRequestedAction = Extract<
  ActionTypes,
  PayloadAction<DeleteCurrentCartRequestedPayload, typeof deleteCurrentCartRequestedType>
>;

export const deleteCurrentCartSucceededType = 'carts/deleteCurrentCartSucceeded';
export type DeleteCurrentCartSucceededAction = Extract<
  ActionTypes,
  PayloadAction<DeleteCurrentCartSucceededPayload, typeof deleteCurrentCartSucceededType>
>;

export const addDiscountCodeRequestedType = 'carts/addDiscountCodeRequested';
export type AddDiscountCodeRequestedAction = Extract<
  ActionTypes,
  PayloadAction<AddDiscountCodeRequestedPayload, typeof addDiscountCodeRequestedType>
>;

export const addDiscountCodeSucceededType = 'carts/addDiscountCodeSucceeded';
export type AddDiscountCodeSucceededAction = Extract<
  ActionTypes,
  PayloadAction<AddDiscountCodeSucceededPayload, typeof addDiscountCodeSucceededType>
>;

export const addDiscountCodeFailedType = 'carts/addDiscountCodeFailed';
export type AddDiscountCodeFailedAction = Extract<
  ActionTypes,
  PayloadAction<AddDiscountCodeFailedPayload, typeof addDiscountCodeFailedType>
>;

export const addDiscountCodeToCurrentCartsRequestedType =
  'carts/addDiscountCodeToCurrentCartsRequested';
export type AddDiscountCodeToCurrentCartsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    AddDiscountCodeToCurrentCartsRequestedPayload,
    typeof addDiscountCodeToCurrentCartsRequestedType
  >
>;

export const addDiscountCodeToCurrentCartsSucceededType =
  'carts/addDiscountCodeToCurrentCartsSucceeded';
export type AddDiscountCodeToCurrentCartsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    AddDiscountCodeToCurrentCartsSucceededPayload,
    typeof addDiscountCodeToCurrentCartsSucceededType
  >
>;

export const addDiscountCodeToCurrentCartsFailedType = 'carts/addDiscountCodeToCurrentCartsFailed';
export type AddDiscountCodeToCurrentCartsFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    AddDiscountCodeToCurrentCartsFailedPayload,
    typeof addDiscountCodeToCurrentCartsFailedType
  >
>;

export const addEmployeeDiscountCodeToCurrentCartsRequestedType =
  'carts/addEmployeeDiscountCodeToCurrentCartsRequested';
export type AddEmployeeDiscountCodeToCurrentCartsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    AddEmployeeDiscountCodeToCurrentCartsRequestedPayload,
    typeof addEmployeeDiscountCodeToCurrentCartsRequestedType
  >
>;

export const addEmployeeDiscountCodeToCurrentCartsSucceededType =
  'carts/addEmployeeDiscountCodeToCurrentCartsSucceeded';
export type AddEmployeeDiscountCodeToCurrentCartsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    AddEmployeeDiscountCodeToCurrentCartsSucceededPayload,
    typeof addEmployeeDiscountCodeToCurrentCartsSucceededType
  >
>;

export const addEmployeeDiscountCodeToCurrentCartsFailedType =
  'carts/addEmployeeDiscountCodeToCurrentCartsFailed';
export type AddEmployeeDiscountCodeToCurrentCartsFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    AddEmployeeDiscountCodeToCurrentCartsFailedPayload,
    typeof addEmployeeDiscountCodeToCurrentCartsFailedType
  >
>;

export const removeDiscountCodeRequestedType = 'carts/removeDiscountCodeRequested';
export type RemoveDiscountCodeRequestedAction = Extract<
  ActionTypes,
  PayloadAction<RemoveDiscountCodeRequestedPayload, typeof removeDiscountCodeRequestedType>
>;

export const removeDiscountCodeSucceededType = 'carts/removeDiscountCodeSucceeded';
export type RemoveDiscountCodeSucceededAction = Extract<
  ActionTypes,
  PayloadAction<RemoveDiscountCodeSucceededPayload, typeof removeDiscountCodeSucceededType>
>;

export const removeDiscountCodeFailedType = 'carts/removeDiscountCodeFailed';
export type RemoveDiscountCodeFailedAction = Extract<
  ActionTypes,
  PayloadAction<RemoveDiscountCodeFailedPayload, typeof removeDiscountCodeFailedType>
>;

export const removeDiscountCodeFromCartsRequestedType =
  'carts/removeDiscountCodeFromCartsRequested';
export type RemoveDiscountCodeFromCartsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveDiscountCodeFromCartsRequestedPayload,
    typeof removeDiscountCodeFromCartsRequestedType
  >
>;

export const removeDiscountCodeFromCartsSucceededType =
  'carts/removeDiscountCodeFromCartsSucceeded';
export type RemoveDiscountCodeFromCartsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveDiscountCodeFromCartsSucceededPayload,
    typeof removeDiscountCodeFromCartsSucceededType
  >
>;

export const removeDiscountCodeFromCartsFailedType = 'carts/removeDiscountCodeFromCartsFailed';
export type RemoveDiscountCodeFromCartsFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveDiscountCodeFromCartsFailedPayload,
    typeof removeDiscountCodeFromCartsFailedType
  >
>;

export const removeAllDiscountCodesFromCartsRequestedType =
  'carts/removeAllDiscountCodesFromCartsRequested';
export type RemoveAllDiscountCodesFromCartsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveAllDiscountCodesFromCartsRequestedPayload,
    typeof removeAllDiscountCodesFromCartsRequestedType
  >
>;

export const removeAllDiscountCodesFromCartsSucceededType =
  'carts/removeAllDiscountCodesFromCartsSucceeded';
export type RemoveAllDiscountCodesFromCartsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveAllDiscountCodesFromCartsSucceededPayload,
    typeof removeAllDiscountCodesFromCartsSucceededType
  >
>;

export const removeAllDiscountCodesFromCartsFailedType =
  'carts/removeAllDiscountCodesFromCartsFailed';
export type RemoveAllDiscountCodesFromCartsFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    RemoveAllDiscountCodesFromCartsFailedPayload,
    typeof removeAllDiscountCodesFromCartsFailedType
  >
>;

export const addGiftCardToCartRequestedType = 'carts/addGiftCardToCartRequested';
export type AddGiftCardToCartRequestedAction = Extract<
  ActionTypes,
  PayloadAction<AddGiftCardToCartRequestedPayload, typeof addGiftCardToCartRequestedType>
>;

export const addGiftCardToCartSucceededType = 'carts/addGiftCardToCartSucceeded';
export type AddGiftCardToCartSucceededAction = Extract<
  ActionTypes,
  PayloadAction<AddGiftCardToCartSucceededPayload, typeof addGiftCardToCartSucceededType>
>;

export const addGiftCardToCartFailedType = 'carts/addGiftCardToCartFailed';
export type AddGiftCardToCartFailedAction = Extract<
  ActionTypes,
  PayloadAction<AddGiftCardToCartFailedPayload, typeof addGiftCardToCartFailedType>
>;

export const removeGiftCardFromCartRequestedType = 'carts/removeGiftCardFromCartRequested';
export type RemoveGiftCardFromCartRequestedAction = Extract<
  ActionTypes,
  PayloadAction<RemoveGiftCardFromCartRequestedPayload, typeof removeGiftCardFromCartRequestedType>
>;

export const removeGiftCardFromCartSucceededType = 'carts/removeGiftCardFromCartSucceeded';
export type RemoveGiftCardFromCartSucceededAction = Extract<
  ActionTypes,
  PayloadAction<RemoveGiftCardFromCartSucceededPayload, typeof removeGiftCardFromCartSucceededType>
>;

export const removeGiftCardFromCartFailedType = 'carts/removeGiftCardFromCartFailed';
export type RemoveGiftCardFromCartFailedAction = Extract<
  ActionTypes,
  PayloadAction<RemoveGiftCardFromCartFailedPayload, typeof removeGiftCardFromCartFailedType>
>;

export const changeCurrentCartEmailRequestedType = 'carts/changeCurrentCartEmailRequested';
export type ChangeCurrentCartEmailRequestedAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartEmailRequestedPayload, typeof changeCurrentCartEmailRequestedType>
>;

export const changeCurrentCartEmailSucceededType = 'carts/changeCurrentCartEmailSucceeded';
export type ChangeCurrentCartEmailSucceededAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartEmailSucceededPayload, typeof changeCurrentCartEmailSucceededType>
>;

export const changeCurrentCartEmailFailedType = 'carts/changeCurrentCartEmailFailed';
export type ChangeCurrentCartEmailFailedAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartEmailFailedPayload, typeof changeCurrentCartEmailFailedType>
>;

export const changeCurrentCartContactRequestedType = 'carts/changeCurrentCartContactRequested';
export type ChangeCurrentCartContactRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartContactRequestedPayload,
    typeof changeCurrentCartContactRequestedType
  >
>;

export const changeCurrentCartContactSucceededType = 'carts/changeCurrentCartContactSucceeded';
export type ChangeCurrentCartContactSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartContactSucceededPayload,
    typeof changeCurrentCartContactSucceededType
  >
>;

export const changeCurrentCartContactFailedType = 'carts/changeCurrentCartContactFailed';
export type ChangeCurrentCartContactFailedAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartContactFailedPayload, typeof changeCurrentCartContactFailedType>
>;

export const changeCurrentCartBirthDateRequestedType = 'carts/changeCurrentCartBirthDateRequested';
export type ChangeCurrentCartBirthDateRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartBirthDateRequestedPayload,
    typeof changeCurrentCartBirthDateRequestedType
  >
>;

export const changeCurrentCartBirthDateSucceededType = 'carts/changeCurrentCartBirthDateSucceeded';
export type ChangeCurrentCartBirthDateSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartBirthDateSucceededPayload,
    typeof changeCurrentCartBirthDateSucceededType
  >
>;

export const changeCurrentCartBirthDateFailedType = 'carts/changeCurrentCartBirthDateFailed';
export type ChangeCurrentCartBirthDateFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartBirthDateFailedPayload,
    typeof changeCurrentCartBirthDateFailedType
  >
>;

export const changeCurrentCartDeliveryAddressRequestedType =
  'carts/changeCurrentCartDeliveryAddressRequested';
export type ChangeCurrentCartDeliveryAddressRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartDeliveryAddressRequestedPayload,
    typeof changeCurrentCartDeliveryAddressRequestedType
  >
>;

export const changeCurrentCartDeliveryAddressSucceededType =
  'carts/changeCurrentCartDeliveryAddressSucceeded';
export type ChangeCurrentCartDeliveryAddressSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartDeliveryAddressSucceededPayload,
    typeof changeCurrentCartDeliveryAddressSucceededType
  >
>;

export const changeCurrentCartDeliveryAddressFailedType =
  'carts/changeCurrentCartDeliveryAddressFailed';
export type ChangeCurrentCartDeliveryAddressFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartDeliveryAddressFailedPayload,
    typeof changeCurrentCartDeliveryAddressFailedType
  >
>;

export const changeCurrentCartSelectedStoreRequestedType =
  'carts/changeCurrentCartSelectedStoreRequested';
export type ChangeCurrentCartSelectedStoreRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartSelectedStoreRequestedPayload,
    typeof changeCurrentCartSelectedStoreRequestedType
  >
>;

export const changeCurrentCartDeliveryModeRequestedType =
  'carts/changeCurrentCartDeliveryModeRequested';
export type ChangeCurrentCartDeliveryModeRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartDeliveryModeRequestedPayload,
    typeof changeCurrentCartDeliveryModeRequestedType
  >
>;

export const changeCurrentCartPaymentModeRequestedType =
  'carts/changeCurrentCartPaymentModeRequested';
export type ChangeCurrentCartPaymentModeRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartPaymentModeRequestedPayload,
    typeof changeCurrentCartPaymentModeRequestedType
  >
>;

export const changeCurrentCartTypeRequestedType = 'carts/changeCurrentCartTypeRequested';
export type ChangeCurrentCartTypeRequestedAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartTypeRequestedPayload, typeof changeCurrentCartTypeRequestedType>
>;

export const changeCurrentCartTypeSucceededType = 'carts/changeCurrentCartTypeSucceeded';
export type ChangeCurrentCartTypeSucceededAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartTypeSucceededPayload, typeof changeCurrentCartTypeSucceededType>
>;

export const changeCurrentCartTypeFailedType = 'carts/changeCurrentCartTypeFailed';
export type ChangeCurrentCartTypeFailedAction = Extract<
  ActionTypes,
  PayloadAction<ChangeCurrentCartTypeFailedPayload, typeof changeCurrentCartTypeFailedType>
>;

export const changeCurrentCartPaymentAddressRequestedType =
  'carts/changeCurrentCartPaymentAddressRequested';
export type ChangeCurrentCartPaymentAddressRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartPaymentAddressRequestedPayload,
    typeof changeCurrentCartPaymentAddressRequestedType
  >
>;

export const changeCurrentCartPaymentAddressSucceededType =
  'carts/changeCurrentCartPaymentAddressSucceeded';
export type ChangeCurrentCartPaymentAddressSucceededAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartPaymentAddressSucceededPayload,
    typeof changeCurrentCartPaymentAddressSucceededType
  >
>;

export const changeCurrentCartPaymentAddressFailedType =
  'carts/changeCurrentCartPaymentAddressFailed';
export type ChangeCurrentCartPaymentAddressFailedAction = Extract<
  ActionTypes,
  PayloadAction<
    ChangeCurrentCartPaymentAddressFailedPayload,
    typeof changeCurrentCartPaymentAddressFailedType
  >
>;

export const placeCurrentCartsRequestedType = 'carts/placeCurrentCartsRequested';
export type PlaceCurrentCartsRequestedAction = Extract<
  ActionTypes,
  PayloadAction<PlaceCurrentCartsRequestedPayload, typeof placeCurrentCartsRequestedType>
>;

export const placeCurrentCartsSucceededType = 'carts/placeCurrentCartsSucceeded';
export type PlaceCurrentCartsSucceededAction = Extract<
  ActionTypes,
  PayloadAction<PlaceCurrentCartsSucceededPayload, typeof placeCurrentCartsSucceededType>
>;

export const placeCurrentCartsFailedType = 'carts/placeCurrentCartsFailed';
export type PlaceCurrentCartsFailedAction = Extract<
  ActionTypes,
  PayloadAction<PlaceCurrentCartsFailedPayload, typeof placeCurrentCartsFailedType>
>;

export const registerCustomerAndChangeAddressesAndSignInRequestedType =
  'carts/registerCustomerAndChangeAddressesAndSignInRequested';
export type RegisterCustomerAndChangeAddressesAndSignInRequestedAction = Extract<
  ActionTypes,
  PayloadAction<
    RegisterCustomerAndChangeAddressesAndSignInRequestedPayload,
    typeof registerCustomerAndChangeAddressesAndSignInRequestedType
  >
>;
